import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import FooterWithNewsletter from "@/components/ui/footer-column";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { navigateToEnterpriseDemo, navigateToEnterpriseForm } from "@/utils/navigation";
import { Link, useNavigate } from "react-router-dom";
import { getPublicImage } from "@/utils/imageUtils";
import defaultImage from "@/assets/defaultImage.jpg";
import SinglifeCaseStudyPage from "./SinglifeCaseStudyPage";
// API URL setup like DailyCircusPage
let apiUrl = "https://api.gimmefy.ai/v2";

const baseUrl = window.location.origin;
if (baseUrl.includes("localhost") || baseUrl.includes("dev.gimmefy.ai")) {
  apiUrl = "https://apidev.gimmefy.ai/v2";
} else if (baseUrl.includes("staging.gimmefy.ai")) {
  apiUrl = "https://apistaging.gimmefy.ai/v2";
}

interface CaseStudy {
  id: string;
  clientName: string;
  logo: string;
  headline: string;
  summary: string;
  image?: string;
  link: string;
}

// Fallback case studies (used if API fails or while loading)
const fallbackCaseStudies: CaseStudy[] = [
  {
    id: "singlife",
    clientName: "Singlife",
    logo: "/lovable-uploads/singlife-logo.svg",
    headline: "How Singlife Scaled Creative Across 100+ SKUs",
    summary: "Batch-created campaign assets across 100+ insurance products using gimmefy",
    link: "/customer-stories?slug=singlife"
  },
  {
    id: "dbs",
    clientName: "DBS",
    logo: "/lovable-uploads/dbs-logo.svg",
    headline: "DBS Accelerates Campaign Creation by 70%",
    summary: "Streamlined multi-channel campaigns with gimmefy Enterprise",
    link: "/customer-stories?slug=dbs"
  },
  {
    id: "volvo",
    clientName: "Volvo",
    logo: "/lovable-uploads/volvo-logo.svg",
    headline: "Volvo Maintains Brand Consistency Across Markets",
    summary: "Unified creative workflows across APAC with centralized control",
    link: "/customer-stories?slug=volvo"
  },
  {
    id: "unilever",
    clientName: "Unilever",
    logo: "/lovable-uploads/6f72231a-02bb-4284-8f6b-be1f16570b92.png",
    headline: "Unilever Powers 50+ Brands with One Platform",
    summary: "Scaled content production while maintaining brand guidelines",
    link: "/customer-stories?slug=unilever"
  },
  {
    id: "motul",
    clientName: "Motul",
    logo: "/lovable-uploads/befabc60-5300-4214-b167-e916f1447aef.png",
    headline: "Motul Drives Performance Marketing at Scale",
    summary: "Generated high-impact motorsports content in record time",
    link: "/customer-stories?slug=motul"
  },
  {
    id: "ntuc",
    clientName: "NTUC",
    logo: "/lovable-uploads/be1816ae-5bdb-49f2-b6cd-d41288611476.png",
    headline: "NTUC Engages Communities with Localized Content",
    summary: "Created culturally relevant campaigns across Singapore",
    link: "/customer-stories?slug=ntuc"
  }
];

const CustomerStoriesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('slug');
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>(fallbackCaseStudies);
  const [_isLoading, setIsLoading] = useState(true);

  // Helper function to extract client name from headline
  const extractClientName = (name: string): string => {
    // Try to extract client name from patterns like "Singlife Uses..." or "DBS Accelerates..."
    const match = name.match(/^([A-Z][a-zA-Z0-9\s&]+?)\s+(Uses|Accelerates|Maintains|Powers|Drives|Engages)/);
    if (match) {
      return match[1].trim();
    }
    // Fallback: take first word or first few words
    const words = name.split(' ');
    if (words.length > 0) {
      return words[0];
    }
    return 'Client';
  };

  // Fetch case studies from API
  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        setIsLoading(true);

        // Make API call to public endpoint (no authentication required)
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };

        // Build query parameters
        const params = new URLSearchParams({
          'filters[group_name]': 'MANAGE_CASE_STUDY',
          'sort': 'display_order'
        });

        const response = await fetch(`${apiUrl}/contents/public?${params}`, {
          method: 'GET',
          headers
        });

        if (!response.ok) {
          throw new Error('Failed to fetch case studies');
        }

        const data = await response.json();
        console.log("Raw API Data:", data);

        // Transform API data to match our CaseStudy interface
        if (data.payload && Array.isArray(data.payload)) {
          const transformedCaseStudies: CaseStudy[] = data.payload.map((item: Record<string, unknown>) => {
            // Parse the JSON content string
            let parsedContent: Record<string, unknown> = {};
            try {
              if (item.content && typeof item.content === 'string') {
                parsedContent = JSON.parse(item.content as string);
              }
            } catch {
              // Error parsing content JSON
            }

            // Extract data from parsed content
            const caseStudyDescription = parsedContent.case_study_description as string || '';
            const name = (item.name as string) || '';
            const clientName = (item.name2 as string) || extractClientName(name);

            // Build image URL
            const imageUrl = item.image
              ? getPublicImage('frontend-files', item.image as string) || "/lovable-uploads/placeholder.svg"
              : "/lovable-uploads/placeholder.svg";

            // Build link from url_slug using query params
            const urlSlug = (item.url_slug as string) || '';
            const link = urlSlug ? `/customer-stories?slug=${urlSlug}` : `/customer-stories?slug=${item.id}`;

            return {
              id: String(item.id || ''),
              clientName: clientName,
              logo: imageUrl,
              headline: name,
              summary: caseStudyDescription,
              image: imageUrl,
              link: link
            };
          });

          // Remove duplicates based on url_slug, keeping the one with the latest created_at or highest id
          const uniqueCaseStudiesMap = new Map<string, CaseStudy>();

          data.payload.forEach((item: Record<string, unknown>, index: number) => {
            const urlSlug = (item.url_slug as string) || String(item.id || '');
            const transformed = transformedCaseStudies[index];

            if (!transformed) return;

            const existing = uniqueCaseStudiesMap.get(urlSlug);

            if (!existing) {
              // No existing entry, add this one
              uniqueCaseStudiesMap.set(urlSlug, transformed);
            } else {
              // Compare and keep the one with later created_at or higher id
              const currentCreatedAt = item.created_at ? new Date(item.created_at as string).getTime() : 0;
              const existingItem = data.payload.find((p: Record<string, unknown>) => {
                const pSlug = (p.url_slug as string) || String(p.id || '');
                return pSlug === urlSlug && transformed.id === String(p.id);
              });
              const existingCreatedAt = existingItem && existingItem.created_at
                ? new Date(existingItem.created_at as string).getTime()
                : 0;

              if (currentCreatedAt > existingCreatedAt ||
                (currentCreatedAt === existingCreatedAt && Number(item.id) > Number(existingItem?.id || 0))) {
                uniqueCaseStudiesMap.set(urlSlug, transformed);
              }
            }
          });

          const uniqueCaseStudies = Array.from(uniqueCaseStudiesMap.values());
          console.log("Processed Case Studies:", uniqueCaseStudies);

          if (uniqueCaseStudies.length > 0) {
            setCaseStudies(uniqueCaseStudies);
          } else {
            setCaseStudies(fallbackCaseStudies);
          }
        }

      } catch {
        // Keep fallback case studies on error
        setCaseStudies(fallbackCaseStudies);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCaseStudies();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };



  if (slug) {
    return <SinglifeCaseStudyPage />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
        {/* Background Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="container mx-auto px-6 py-24 md:py-32 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-block mb-6"
            >
              <Badge variant="outline" className="px-4 py-2 text-sm font-medium border-primary/20 bg-primary/5">
                Customer Stories
              </Badge>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-br from-foreground via-foreground to-primary bg-clip-text text-transparent pb-2"
            >
              See how leading brands scale smarter, create faster, and stay on-brand with gimmefy
            </motion.h1>

            {/* Supporting Paragraph */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed max-w-3xl mx-auto"
            >
              From high-growth disruptors to global enterprises, marketers are transforming how they work with gimmefy. Explore how teams at Singlife, DBS, Volvo, Unilever, Motul, NTUC, and more are using gimmefy Enterprise to simplify workflows, accelerate campaigns, and build consistent, high-impact content — at scale.
            </motion.p>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Button
                size="lg"
                className="group"
                onClick={() => navigateToEnterpriseDemo(navigate)}
              >
                Book a free demo
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Case Study Cards Grid Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-6">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {caseStudies.map((study) => (
              <motion.div
                key={study.id}
              // variants={itemVariants}
              // whileHover={{ scale: 1.05, y: -8 }}
              // transition={{ duration: 0.3 }}
              >
                <Link to={study.link} className="block h-full">
                  <Card className="h-full hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30 group cursor-pointer">
                    <CardHeader className="space-y-4">
                      {/* Client Logo */}
                      <div className="h-16 flex items-center justify-start">
                        <img
                          src={study.logo}
                          alt={`${study.clientName} logo`}
                          className="h-12 object-contain"
                          onError={(e) => {
                            e.currentTarget.src = defaultImage;
                          }}
                        />
                      </div>

                      {/* Headline */}
                      <CardTitle className="text-xl md:text-2xl leading-tight group-hover:text-primary transition-colors">
                        {study.headline}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      {/* Summary */}
                      <CardDescription className="text-base leading-relaxed">
                        {study.summary}
                      </CardDescription>

                      {/* Icon with checkmark */}
                      <div className="flex items-center gap-2 text-primary">
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="text-sm font-medium">Success Story</span>
                      </div>

                      {/* Read More Link */}
                      <div className="inline-flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-all">
                        Read more
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="relative overflow-hidden py-24 bg-gradient-to-br from-purple-50 via-orange-50 to-purple-100 dark:from-purple-950/20 dark:via-orange-950/20 dark:to-purple-950/20">
        {/* Background Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl mx-auto text-center"
          >
            {/* Headline */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-br from-foreground via-foreground to-primary bg-clip-text text-transparent pb-2">
              Ready to power your Enterprise with gimmefy?
            </h2>

            {/* Subtext */}
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              Join leading brands transforming their content workflows
            </p>

            {/* CTA Button */}
            <Button
              size="lg"
              className="group"
              onClick={() => navigateToEnterpriseForm(navigate)}
            >
              Get started today
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      <FooterWithNewsletter />
    </div>
  );
};

export default CustomerStoriesPage;
